/* ============================================================
   The one breakpoint the layout turns on. Below it (a phone, or a desktop
   window dragged to half a screen) the sidebar becomes a top bar and a tab
   bar, the sky shortens, dialogs become bottom sheets. The CSS side is
   Tailwind's `md:` (768px); this is the same line for the few decisions
   made in JS (the sky's height, the phone nudge).
   ============================================================ */
import { useEffect, useState } from 'react';

export const NARROW_QUERY = '(max-width: 767.98px)';
const COARSE_QUERY = '(pointer: coarse)';

function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const apply = () => setOn(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [query]);
  return on;
}

export function useNarrow(): boolean {
  return useMedia(NARROW_QUERY);
}

/** A narrow window AND a finger: a phone, not a squeezed desktop window. */
export function usePhone(): boolean {
  // both hooks on every render: `a && b` would skip the second one while the
  // window is wide, and the first resize to narrow would then add a hook
  // mid-life — "rendered more hooks than during the previous render"
  const narrow = useMedia(NARROW_QUERY);
  const coarse = useMedia(COARSE_QUERY);
  return narrow && coarse;
}
